import CryptoJS from "crypto-js";
import { type CipherInfoOptions } from "crypto";

/**
 * AESCryptoService provides AES-based encryption and decryption utilities.
 *
 * This class wraps CryptoJS to offer a cleaner API for encrypting and decrypting
 * data. It supports both a global default encryption key (from environment variables)
 * and an instance-specific override key.
 *
 * Typical usage:
 *   const crypto = new AESCryptoService();
 *   const encrypted = crypto.encrypt("Awesome sites");
 *   const decrypted = crypto.decrypt(encrypted);
 *
 */
export class AESCryptoService {
  private static readonly privateKey: string = process.env.REACT_APP_ENCRYPTION_KEY || "";
  private customPrivateKey: string | undefined;

  constructor(key?: string) {
    if (key) {
      this.customPrivateKey = key;
    }
  }

  /**
   * Encrypts data using AES encryption.
   *
   * @param {unknown} data - The data to encrypt. It will be stringified internally.
   * @param {CipherInfoOptions} [option] - Optional AES configuration settings.
   * @returns CryptoJS.lib.CipherParams - Encrypted cipher parameters.
   */
  encrypt(data: string, option?: CipherInfoOptions): CryptoJS.lib.CipherParams {
    if (!data) throw new Error("No data provided");
    return CryptoJS.AES.encrypt(data, this.customPrivateKey || AESCryptoService.privateKey, option);
  }

  /**
   * Decrypts data using AES encryption.
   *
   * @param {CryptoJS.lib.CipherParams | string} encryptedData - The encrypted data to decrypt.
   * @param {CipherInfoOptions} [option] - Optional AES configuration settings.
   * @returns string The decrypted data.
   * @example
   * const decrypted = decrypt(encryptedString, { key: 'myKey' });
   */
  decrypt(encryptedData: CryptoJS.lib.CipherParams | string, option?: CipherInfoOptions): string {
    if (!encryptedData) throw new Error("No data provided");
    const decryptedToken = CryptoJS.AES.decrypt(encryptedData, this.customPrivateKey || AESCryptoService.privateKey, option);
    return decryptedToken.toString(CryptoJS.enc.Utf8);
  }
}
